import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Trash2, Barcode, Plus } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import { useStockRollStore } from "../store/stockrollStore";
import { Dropdown } from "primereact/dropdown";
import api from "../service/api";


interface RollTransactionModalProps {
    isOpen: boolean;
    onClose: (data?: any[]) => void;
    type: 'IN' | 'OUT'; // Giriş mi çıkış mı?
    initialRolls: any[]; // Mevcut kayıtlar
    stockId: number;
    warehouseId: number;
    variantId?: number | null;
}

export const RollTransactionModal: React.FC<RollTransactionModalProps> = ({
    isOpen, onClose, type, initialRolls, stockId, warehouseId, variantId
}) => {
    const { user } = useAuthStore();
    const rollStore = useStockRollStore();

    const [rolls, setRolls] = useState<any[]>(initialRolls || []);
    const [tempBarcode, setTempBarcode] = useState("");
    const [newRollQty, setNewRollQty] = useState(0);

    const [availableRolls, setAvailableRolls] = useState<any[]>([]);
    const [loadingRolls, setLoadingRolls] = useState(false);

    const [stockVariants, setStockVariants] = useState<any[]>([]);
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(variantId || null);

    useEffect(() => {
        setRolls(initialRolls || []);
        if (isOpen && variantId) {
            setSelectedVariantId(variantId);
        }
    }, [initialRolls, isOpen, variantId]);

    useEffect(() => {
        if (isOpen && stockId) {
            // Fetch stock to get variants
            api.get(`/stock/${stockId}`).then(res => {
                const data = res.data?.data || res.data;
                if (data && data.variants) {
                    setStockVariants(data.variants);
                }
            }).catch(err => console.error("Stok varyantları alınamadı:", err));
        }
    }, [isOpen, stockId]);

    useEffect(() => {
        if (type === 'OUT' && isOpen && stockId && warehouseId) {
            setLoadingRolls(true);
            const filters: any[] = [
                { field: "stockId", operator: "eq", value: stockId },
                { field: "warehouseId", operator: "eq", value: warehouseId },
                { field: "currentQuantity", operator: "gt", value: 0 }
            ];
            
            if (variantId) {
                filters.push({ field: "variantId", operator: "eq", value: variantId });
            }

            rollStore.fetchDataDynamic({
                filter: { logic: "and", filters },
                pageSize: 500
            }).then((res: any) => {
                const fetched = res?.items || res?.data?.items || res?.data || [];

                // Eğer daha önceden (initialRolls) bu faturaya/irsaliyeye eklenmiş bir top varsa onu da listede göster
                const combined = [...fetched];
                (initialRolls || []).forEach(ir => {
                    if (!combined.find(c => c.id === ir.id)) {
                        combined.push({ ...ir, currentQuantity: ir.quantity });
                    }
                });
                setAvailableRolls(combined);
            }).finally(() => setLoadingRolls(false));
        }
    }, [type, isOpen, stockId, warehouseId, variantId]);

    const addRoll = () => {
        if (!newRollQty || newRollQty <= 0) return;
        const newRoll = {
            id: Math.random(),
            barcode: tempBarcode || `${user?.firmName?.slice(0, 3).toUpperCase() || "FRM"}-${Date.now()}`,
            quantity: newRollQty,
            variantId: selectedVariantId,
            isNew: true
        };
        setRolls([...rolls, newRoll]);
        setTempBarcode("");
        setNewRollQty(0);
    };

    const getOutQty = (id: any) => {
        return rolls.find(r => r.id === id)?.quantity || null;
    };

    const handleOutQtyChange = (row: any, val: number | null | undefined) => {
        const numQty = val || 0;
        let updated = [...rolls];
        const existingIdx = updated.findIndex(r => r.id === row.id);

        if (numQty > 0) {
            if (existingIdx >= 0) {
                updated[existingIdx].quantity = numQty;
            } else {
                updated.push({ id: row.id, barcode: row.barcode, quantity: numQty, isNew: false });
            }
        } else {
            if (existingIdx >= 0) {
                updated.splice(existingIdx, 1);
            }
        }
        setRolls(updated);
    };

    return (
        <Dialog
            visible={isOpen}
            onHide={() => onClose(rolls)}
            header={
                <div className="flex items-center gap-2 text-[var(--text)]">
                    <Barcode size={20} />
                    <span className="text-lg font-bold">{type === 'IN' ? "Top Girişi Yap" : "Toplardan Miktar Düş"}</span>
                </div>
            }
            className="w-full sm:w-[600px] md:w-[700px]"
        >
            <div className="flex flex-col gap-4 p-1 mt-2">
                {type === 'IN' ? (
                    <>
                        <div className="flex flex-col sm:flex-row gap-2 p-4 bg-[var(--bg-alt)] border border-[var(--border)] rounded-xl items-center">
                            {stockVariants.length > 0 && (
                                <Dropdown
                                    value={selectedVariantId}
                                    options={stockVariants}
                                    onChange={(e) => setSelectedVariantId(e.value)}
                                    optionLabel="name"
                                    optionValue="id"
                                    placeholder="Varyant Seçin"
                                    disabled={!!variantId} // Eğer dışarıdan geldiyse değiştirilemez
                                    className="w-full sm:w-48 bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]"
                                    showClear={!variantId}
                                />
                            )}
                            <InputText
                                placeholder="Barkod / Top No (Boşsa Oto.)"
                                value={tempBarcode}
                                onChange={(e) => setTempBarcode(e.target.value)}
                                className="w-full sm:flex-1 bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]"
                            />
                            <InputNumber
                                placeholder="Metraj / Kg"
                                value={newRollQty === 0 ? null : newRollQty}
                                onValueChange={(e) => setNewRollQty(e.value || 0)}
                                className="w-full sm:w-32"
                                inputClassName="w-full bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]"
                            />
                            <Button
                                icon={<Plus size={16} />}
                                onClick={addRoll}
                                disabled={!newRollQty || newRollQty <= 0 || (stockVariants.length > 0 && !selectedVariantId && !variantId)}
                                className="w-full sm:w-auto bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white border-none rounded-lg"
                            />
                        </div>

                        <div className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--card)] mt-4">
                            <DataTable value={rolls} className="p-datatable-sm" emptyMessage={<div className="p-4 text-center text-[var(--text)] opacity-60">Henüz top eklenmedi.</div>}>
                                <Column field="barcode" header="Barkod / Top No" body={(row) => (
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-[var(--text)]">{row.barcode}</span>
                                        {row.variantId && stockVariants.find(v => v.id === row.variantId) && (
                                            <span className="text-xs text-[var(--primary)]">{stockVariants.find(v => v.id === row.variantId)?.name}</span>
                                        )}
                                    </div>
                                )} />
                                <Column field="quantity" header="Metraj" body={(row) => (
                                    <InputNumber
                                        value={row.quantity}
                                        onValueChange={(e) => {
                                            const updated = rolls.map(r => r.id === row.id ? { ...r, quantity: e.value } : r);
                                            setRolls(updated);
                                        }}
                                        className="w-full"
                                        inputClassName="w-full max-w-[120px] bg-[var(--input-bg)] border-[var(--input-border)] text-[var(--input-text)]"
                                    />
                                )} />
                                <Column body={(row) => (
                                    <Button icon={<Trash2 size={16} />} className="p-button-text text-[var(--danger)] hover:bg-[var(--danger-hover)] w-8 h-8 p-0 flex items-center justify-center rounded-lg"
                                        onClick={() => setRolls(rolls.filter(r => r.id !== row.id))} />
                                )} style={{ width: '4rem', textAlign: 'center' }} />
                            </DataTable>
                        </div>
                    </>
                ) : (
                    <div className="border border-[var(--border)] rounded-xl overflow-hidden bg-[var(--card)]">
                        <DataTable
                            value={availableRolls}
                            loading={loadingRolls}
                            className="p-datatable-sm"
                            emptyMessage={<div className="p-4 text-center text-[var(--text)] opacity-60">Bu stok ve depo için uygun top bulunamadı.</div>}
                        >
                            <Column field="barcode" header="Barkod / Top No" body={(row) => (
                                <div className="flex flex-col">
                                    <span className="font-semibold text-[var(--text)]">{row.barcode}</span>
                                    {row.variant?.name && (
                                        <span className="text-xs text-[var(--primary)]">{row.variant.name}</span>
                                    )}
                                </div>
                            )} />

                            <Column field="currentQuantity" header="Mevcut Miktar" body={(row) => (
                                <span className="font-bold text-[var(--text)] opacity-70">
                                    {row.currentQuantity ?? row.quantity}
                                </span>
                            )} style={{ width: '8rem' }} />

                            <Column header="Çıkış Miktarı" body={(row) => {
                                const currentOut = getOutQty(row.id);
                                return (
                                    <InputNumber
                                        value={currentOut}
                                        onValueChange={(e) => handleOutQtyChange(row, e.value)}
                                        className="w-full"
                                        max={row.currentQuantity ?? row.quantity} // Mevcuttan fazla çıkılamaz
                                        min={0}
                                        inputClassName={`w-full max-w-[120px] bg-[var(--input-bg)] border-[var(--input-border)] font-bold text-center ${currentOut ? 'text-[var(--primary)]' : 'text-[var(--input-text)]'}`}
                                    />
                                )
                            }} style={{ width: '12rem' }} />
                        </DataTable>
                    </div>
                )}

                <div className="flex justify-between items-center mt-2 pt-4 border-t border-[var(--border)]">
                    <div className="text-sm font-bold text-[var(--text)] opacity-70">
                        Seçilen: <span className="text-[var(--primary)]">{rolls.reduce((acc, curr) => acc + (curr.quantity || 0), 0)}</span> Toplam Miktar
                    </div>
                    <Button label="Tamamla ve Aktar" onClick={() => onClose(rolls)} className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white border-none px-6 py-2 rounded-lg transition-colors shadow-md" />
                </div>
            </div>
        </Dialog>
    );
};